import React, { useCallback, useState, useEffect, useMemo } from 'react';
import _ from 'underscore';
import { Button } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';

export const usePaginationConfig = <Type extends unknown>({
  data,
  pageSize = 5,
  isLoading = false,
  i18n,
}: {
  data: Array<Type>,
  pageSize?: number,
  isLoading?: boolean,
  i18n: any,
}) => {
  const [state, setState] = useState({ current: 0, total: 0, initialized: false });

  useEffect(() => {
    if (!isLoading) {
      if (!_.isEmpty(data)) {
        setState({
          current: 1,
          total: Math.ceil(data.length / pageSize),
          initialized: true,
        });
      } else {
        setState(s => ({ ...s, initialized: true }));
      }
    }
  }, [data, pageSize, isLoading]);

  const getDataToDisplayFromSortedData = useCallback(sortedData => sortedData.slice((state.current - 1) * pageSize, state.current * pageSize), [
    pageSize,
    state,
  ]);

  const onNext = useMemo(() => {
    const next = state.current + 1;
    return next <= state.total ? () => setState({ ...state, current: next }) : _.noop;
  }, [state]);

  const onPrevious = useMemo(() => {
    const previous = state.current - 1;
    return previous > 0 ? () => setState({ ...state, current: previous }) : _.noop;
  }, [state]);

  const renderPagination = useCallback(() => {
    if (data?.length > 0) {
      return (
        <div className="pagination-container">
          <span className="ba-card-pagination-text">
            {i18n.getString(_TL_('{{current}} of {{total}}'), { current: state.current, total: state.total })}
          </span>
          <Button
            className="ba-card-pagination-previous"
            onClick={onPrevious as React.MouseEventHandler<HTMLButtonElement>}
            disabled={state.current <= 1}
            icon={<ChevronLeftRegular />}
            appearance="transparent"
            aria-label={i18n.getString('Previous')}
          />
          <Button
            className="ba-card-pagination-next"
            onClick={onNext as React.MouseEventHandler<HTMLButtonElement>}
            disabled={state.current >= state.total}
            icon={<ChevronRightRegular />}
            appearance="transparent"
            aria-label={i18n.getString('Next')}
          />
        </div>
      );
    }
    return null;
  }, [data, i18n, onNext, onPrevious, state]);

  return {
    state,
    getDataToDisplayFromSortedData,
    resetToFirstPage: () => setState({ ...state, current: 1 }),
    renderPagination,
  };
};
